import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import NoDataMessage from "../../../common/NoDataMessage";
import {
  PlatformMemberChangeResult,
  PlatformTeamRole,
  PlatformTeamUserRow,
} from "../../../common/platformTypes";
import { reportSeatChange } from "../../../common/seatChange";
import { countSeats } from "../../../common/seats";
import { Date as DateView } from "../../../components/Date";
import { PlatformUserLink } from "../../../components/DocumentLink";
import {
  DocumentTable,
  DocumentTableProperty,
} from "../../../components/DocumentTable";
import { ToolbarButton } from "../../../components/ToolbarButton";
import {
  teamMemberAdd,
  teamMemberRemove,
  teamMemberRole,
} from "../../../services/api/Platform";
import { AddMemberModal } from "./AddMemberModal";

type Props = {
  teamId: string;
  members: PlatformTeamUserRow[];
  /** From the team's subscription, so an add can warn about going over quota. */
  licenses?: number;
  reload: () => Promise<void>;
};

/**
 * Membership belongs to the team, not to its subscription — a team can have
 * members and no subscription at all. Seats are derived from the roles set
 * here, which is the only connection back to billing.
 */
export const TeamMembersTab: React.FunctionComponent<Props> = ({
  teamId,
  members,
  licenses,
  reload,
}) => {
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // `reload()` flips the page back to its loading state, which unmounts this
  // tab — so the `finally` below would otherwise set state on a dead component.
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  /**
   * Seats are checked twice, on purpose. Here, so the operator is asked
   * before a request goes out and the answer names the real numbers; and
   * again on the server, which is the side that actually decides and which
   * still refuses if this view was stale. `rolesAfter` is what the team's
   * roles would be once the change lands.
   */
  function overQuota(rolesAfter: PlatformTeamRole[]): number | undefined {
    if (licenses === undefined) {
      return undefined;
    }
    const after = countSeats(rolesAfter);
    return after > licenses ? after : undefined;
  }

  /**
   * `rethrow` is for the callers that live inside a modal: they need the
   * refusal to reach the form so it can stay open and show it, rather than a
   * toast over a page the operator has already been thrown out of. Returns
   * whether the change was actually applied — a declined confirmation is
   * neither a success nor an error.
   */
  async function run(
    action: (acknowledged: boolean) => Promise<PlatformMemberChangeResult>,
    rolesAfter?: PlatformTeamRole[],
    rethrow = false,
  ): Promise<boolean> {
    const wouldBe = rolesAfter ? overQuota(rolesAfter) : undefined;
    if (
      wouldBe !== undefined &&
      !window.confirm(
        `This takes seats to ${wouldBe}, past the ${licenses} licences purchased.`,
      )
    ) {
      return false;
    }

    setBusy(true);
    try {
      let result: PlatformMemberChangeResult;
      try {
        result = await action(wouldBe !== undefined);
      } catch (e) {
        // Backstop: this view can be stale, so the server may refuse a change
        // the check above let through.
        const message = (e as Error).message;
        if (!message.includes("Re-submit to confirm")) {
          throw e;
        }
        if (!window.confirm(message.replace(" Re-submit to confirm.", ""))) {
          return false;
        }
        result = await action(true);
      }
      reportSeatChange(result, licenses);
      await reload();
      return true;
    } catch (e) {
      if (rethrow) {
        throw e;
      }
      toast.error((e as Error).message);
      return false;
    } finally {
      if (mounted.current) {
        setBusy(false);
      }
    }
  }

  const properties: DocumentTableProperty<PlatformTeamUserRow>[] = [
    {
      title: "Id",
      render: (d) => (
        <PlatformUserLink docId={d.userId}>
          <span className="text-monospace">{d.userId}</span>
        </PlatformUserLink>
      ),
      style: { maxWidth: "30%" },
    },
    {
      title: "Email",
      render: (d) => d.email ?? <span className="text-muted">—</span>,
    },
    { title: "Name", render: (d) => d.name },
    {
      title: "Seat Assigned",
      render: (d) =>
        d.licenseAssignedAt ? (
          <DateView date={new Date(d.licenseAssignedAt)} />
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      title: "Role",
      // Owner is fixed: it tracks the team's ownerId, not a membership row.
      render: (d) =>
        d.role === "owner" ? (
          <span className="text-capitalize">owner</span>
        ) : (
          <select
            className="form-control form-control-sm"
            style={{ width: "7rem" }}
            value={d.role}
            disabled={busy}
            onChange={(e) => {
              const role = e.currentTarget.value as PlatformTeamRole;
              run(
                (acknowledgeSeatChange) =>
                  teamMemberRole({
                    teamId,
                    userId: d.userId,
                    role,
                    acknowledgeSeatChange,
                  }),
                members.map((m) => (m.userId === d.userId ? role : m.role)),
              );
            }}
          >
            <option value="viewer">viewer</option>
            <option value="editor">editor</option>
          </select>
        ),
    },
    {
      title: "",
      render: (d) =>
        d.role === "owner" ? null : (
          <button
            className="btn btn-sm btn-outline-danger"
            disabled={busy}
            onClick={() => {
              if (!window.confirm(`Remove ${d.email ?? d.userId}?`)) {
                return;
              }
              run(() => teamMemberRemove({ teamId, userId: d.userId }));
            }}
          >
            Remove
          </button>
        ),
    },
  ];

  return (
    <>
      <div className="btn-group mt-3">
        <ToolbarButton
          className="btn-outline-success"
          icon="fa-user-plus"
          title="Add Member"
          onClick={() => setAddOpen(true)}
        />
      </div>
      <AddMemberModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={async ({ identifier, role }) => {
          const applied = await run(
            (acknowledgeSeatChange) =>
              teamMemberAdd({
                teamId,
                identifier,
                role,
                acknowledgeSeatChange,
              }),
            [...members.map((m) => m.role), role],
            true,
          );
          if (applied) {
            setAddOpen(false);
          }
        }}
      />
      <div className="card bg-dark mt-2">
        {members.length ? (
          <DocumentTable<PlatformTeamUserRow>
            objects={members}
            properties={properties}
          />
        ) : (
          <NoDataMessage componentName="member" />
        )}
      </div>
    </>
  );
};
